/**
 * PATH: packages/job-hub/src/jobs/client/resolveScheduleRoutes.ts
 * FILE_ID: JOBHUB-JOBS-CLIENT-SCHED-001
 * PURPOSE: Route-table segment 2 — the platform job-schedule board surface
 *          (people, phases, templates, areas, calendar/company days, schedule
 *          items + every literal sub-route that must beat /schedule/:id).
 * WHY: Moved VERBATIM from djangoJobHubClient.ts (S4 rank 12). ORDER INSIDE
 *      THIS FILE IS LOAD-BEARING: literal segments (seed/structure/bulk/
 *      actions/dependencies/documents/board-settings/…) must precede their
 *      :id catch-alls — the documented sam-api route-order traps.
 */
import type { SegmentResolver } from './routeTypes'
import { ID } from './routeTypes'

export const resolveScheduleRoutes: SegmentResolver = ({ verb, match, tenantParam }) => {
  // --- schedule board (platform job-schedule, org-scoped via ?tenant=) -----
  // The shared <JobScheduleBoard> talks generic /schedule + /people; the
  // platform exposes the same ScheduleRow/ScheduleEmployeeOption contract under
  // /api/job-schedule/* (qb!3413, built for this board). Pure URL + tenant
  // remap so every standard-API tenant gets the SAME schedule board SAM uses.
  // Team-tab person card reads /people/:id; the platform serves one org
  // person (superset shape) at /api/job-schedule/people/:id/. Must precede
  // the bare /people match below.
  const person = match(`/people/${ID}`)
  if (person && (verb === 'get' || verb === 'put')) {
    return {
      kind: 'http',
      method: verb,
      url: `/api/job-schedule/people/${person[1]}/`,
      params: tenantParam,
    }
  }
  if (match('/people') && (verb === 'get' || verb === 'post')) {
    return {
      kind: 'http',
      method: verb,
      url: '/api/job-schedule/people/',
      // The board's team rail expects ALL statuses (it filters/strikes
      // inactive people client-side); Django defaults to active-only, so
      // the list read must opt in explicitly.
      params: verb === 'get' ? { ...tenantParam, include_inactive: 1 } : tenantParam,
    }
  }
  // Org workstreams/phases (board lanes) — platform CRUD (qb parity MR).
  const phase = match(`/phases/${ID}`)
  if (phase && (verb === 'put' || verb === 'delete')) {
    return {
      kind: 'http',
      method: verb,
      url: `/api/job-schedule/phases/${phase[1]}/`,
      params: tenantParam,
    }
  }
  if (match('/phases') && (verb === 'get' || verb === 'post')) {
    return { kind: 'http', method: verb, url: '/api/job-schedule/phases/', params: tenantParam }
  }
  // Org task templates (process rail) — Unified Scheduling P1. Seed must
  // precede /templates/:id so the literal "seed" segment isn't eaten as
  // a template id (same route-order trap as /schedule/dependencies).
  if (match('/templates/seed') && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/job-schedule/templates/seed/', params: tenantParam }
  }
  const template = match(`/templates/${ID}`)
  if (template && (verb === 'put' || verb === 'delete')) {
    return {
      kind: 'http',
      method: verb,
      url: `/api/job-schedule/templates/${template[1]}/`,
      params: tenantParam,
    }
  }
  if (match('/templates') && (verb === 'get' || verb === 'post')) {
    return { kind: 'http', method: verb, url: '/api/job-schedule/templates/', params: tenantParam }
  }
  // Job site areas (lanes registry) — Unified Scheduling P2. The literal
  // "structure" segment must precede /areas/:id (route-order trap).
  if (match('/areas/structure') && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/job-schedule/areas/structure/', params: tenantParam }
  }
  const area = match(`/areas/${ID}`)
  if (area && (verb === 'put' || verb === 'delete')) {
    return {
      kind: 'http',
      method: verb,
      url: `/api/job-schedule/areas/${area[1]}/`,
      params: tenantParam,
    }
  }
  if (match('/areas') && (verb === 'get' || verb === 'post')) {
    return { kind: 'http', method: verb, url: '/api/job-schedule/areas/', params: tenantParam }
  }
  // A job's quotes for the shift quote picker (change #1115), ?job_id=.
  // Not '/quotes': that path is the Jobs hub's own quote list.
  if (match('/schedule/quotes') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/job-schedule/quotes/', params: tenantParam }
  }
  // Cross-job double-booking listing (read-only warnings).
  if (match('/conflicts') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/job-schedule/conflicts/', params: tenantParam }
  }
  // Merged day decorations (rain + RDO/public holidays) for the calendars.
  if (match('/calendar-days') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/job-schedule/calendar-days/', params: tenantParam }
  }
  // Company off-days (RDO / public holiday) + per-person overrides (RDO arc).
  // The literal "overrides" sub-route must precede the bare /company-days GET
  // (route-order trap). DELETE carries employee_id+date in config.params, which
  // mergeParams folds together with ?tenant=.
  if (match('/company-days/overrides') && (verb === 'post' || verb === 'delete')) {
    return {
      kind: 'http',
      method: verb,
      url: '/api/job-schedule/company-days/overrides/',
      params: tenantParam,
    }
  }
  if (match('/company-days') && (verb === 'get' || verb === 'post')) {
    return { kind: 'http', method: verb, url: '/api/job-schedule/company-days/', params: tenantParam }
  }
  // Pending and approved payroll leave for the visible board. Must precede
  // /schedule/:id so "leave-requests" is not read as an item id.
  if (match('/leave-requests') && (verb === 'get' || verb === 'post')) {
    return { kind: 'http', method: verb, url: '/api/job-schedule/leave-requests/', params: tenantParam }
  }
  // Rain-day bulk shift — slide the remaining programme N working days.
  if (match('/schedule/bulk-shift') && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/job-schedule/bulk-shift/', params: tenantParam }
  }
  // Multi-select bulk mutate (delete / reassign / shift_dates) — one request
  // for the planner's selection set (qb!3685). Exact-match precedes the
  // /schedule/:id catch so "bulk" is never eaten as an item id.
  if (match('/schedule/bulk') && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/job-schedule/bulk/', params: tenantParam }
  }
  // Top active jobs by recent timesheet hours — powers the 1-5 quick-pick
  // job rail (qb!3685).
  if (match('/schedule/active-jobs') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/job-schedule/active-jobs/', params: tenantParam }
  }
  // Simpro read-only overlay + one-shot cutover importer (P4).
  if (match('/schedule/simpro-overlay') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/job-schedule/simpro-overlay/', params: tenantParam }
  }
  if (match('/schedule/simpro-import') && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/job-schedule/simpro-import/', params: tenantParam }
  }
  // Per-site-area programme roll-up for the BI tab (P6).
  if (match('/schedule/area-progress') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/job-schedule/area-progress/', params: tenantParam }
  }
  // Org sticky default shift window (board hours editor + fast-schedule
  // default inputs read/write it). Exact-match before /schedule/:id so the
  // literal "board-settings" segment isn't eaten as an item id (same
  // route-order trap as /schedule/dependencies + /schedule/bulk).
  if (match('/schedule/board-settings') && (verb === 'get' || verb === 'put')) {
    return { kind: 'http', method: verb, url: '/api/job-schedule/board-settings/', params: tenantParam }
  }
  // Role catalogue is sam-api-only (AI role classification). Answer the
  // read empty and the suggest as a no-op so the onboard modal's
  // best-effort catalogue degrades silently on the standard platform.
  if (match('/roles') && verb === 'get') {
    return { kind: 'synthetic', synthetic: { roles: [], functions: {} } }
  }
  if (match('/roles/suggest') && verb === 'post') {
    return { kind: 'synthetic', synthetic: { ok: true } }
  }
  // Dependency links (FS/SS/FF/SF) — MUST precede /schedule/:id so the
  // literal "dependencies" segment isn't eaten as an item id (the same
  // route-order trap sam-api documents on its router).
  const dep = match(`/schedule/dependencies/${ID}`)
  if (dep && (verb === 'put' || verb === 'delete')) {
    return {
      kind: 'http',
      method: verb,
      url: `/api/job-schedule/dependencies/${dep[1]}/`,
      params: tenantParam,
    }
  }
  if (match('/schedule/dependencies') && (verb === 'get' || verb === 'post')) {
    return { kind: 'http', method: verb, url: '/api/job-schedule/dependencies/', params: tenantParam }
  }
  // Shift documents (SWMS/drawings/permits attached to one shift) — MUST
  // precede /schedule/:id so the "<id>/documents" tail isn't eaten as a bare
  // item id (the same route-order trap as /schedule/dependencies).
  const shiftDoc = match(`/schedule/${ID}/documents/${ID}`)
  if (shiftDoc && verb === 'delete') {
    return {
      kind: 'http',
      method: 'delete',
      url: `/api/job-schedule/items/${shiftDoc[1]}/documents/${shiftDoc[2]}/`,
      params: tenantParam,
    }
  }
  const shiftDocs = match(`/schedule/${ID}/documents`)
  if (shiftDocs && (verb === 'get' || verb === 'post')) {
    return {
      kind: 'http',
      method: verb,
      url: `/api/job-schedule/items/${shiftDocs[1]}/documents/`,
      params: tenantParam,
    }
  }
  // Board action log + Ctrl+Z undo/redo (qb!4302). Exact-match before the
  // /schedule/:id catch so the "actions" segment isn't eaten as an item id
  // (same route-order trap as /schedule/dependencies + /schedule/bulk).
  if (match('/schedule/actions/undo') && verb === 'post') {
    return { kind: 'http', method: 'post', url: '/api/job-schedule/actions/undo/', params: tenantParam }
  }
  if (match('/schedule/actions') && verb === 'get') {
    return { kind: 'http', method: 'get', url: '/api/job-schedule/actions/', params: tenantParam }
  }
  // Settle a flagged local-vs-Simpro disagreement on one mirrored row
  // (keep_local | take_external). Must precede the bare /schedule/:id catch.
  const resolveConflict = match(`/schedule/${ID}/resolve-conflict`)
  if (resolveConflict && verb === 'post') {
    return {
      kind: 'http',
      method: 'post',
      url: `/api/job-schedule/items/${resolveConflict[1]}/resolve-conflict/`,
      params: tenantParam,
    }
  }
  const sched = match(`/schedule/${ID}`)
  if (sched) {
    return {
      kind: 'http',
      method: verb,
      url: `/api/job-schedule/items/${sched[1]}/`,
      params: tenantParam,
    }
  }
  if (match('/schedule') && (verb === 'get' || verb === 'post')) {
    return { kind: 'http', method: verb, url: '/api/job-schedule/items/', params: tenantParam }
  }

  return null
}
